import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import React from 'react';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import type { ANPRData } from './ANPRTable';
import toast from 'react-hot-toast';
import { GLOBAL_TOOLTIP_PROPS } from '../../config/tooltips.config.ts';
import { userTimezoneHeaders } from '../../utils/userTimezoneHeader';

/** Tooltips above MUI Dialog (z-index ~1300); span child = reliable hover target */
const addVehicleFieldTooltipProps = {
  ...GLOBAL_TOOLTIP_PROPS,
  enterDelay: 200,
  placement: 'top' as const,
  slotProps: {
    ...GLOBAL_TOOLTIP_PROPS.slotProps,
    popper: {
      ...GLOBAL_TOOLTIP_PROPS.slotProps?.popper,
      style: { zIndex: 2000 },
    },
  },
};

const USE_CASE_CONFIG_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || '';

/** API response item from GET /use-case-configuration/vehicle-categories */
interface VehicleCategoryApiItem {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * License plate: 5–12 chars from [A-Z0-9 -], at least 2 letters and 2 digits.
 */
const LICENSE_PLATE_REGEX =
  /^(?=(?:.*[A-Z]){2,})(?=(?:.*[0-9]){2,})[A-Z0-9 -]{5,12}$/;
const LICENSE_PLATE_MAX_LEN = 12;

/** Allow only A–Z, 0–9, space, hyphen; uppercase; max length */
function normalizeLicensePlateInput(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9 -]/g, "")
    .slice(0, LICENSE_PLATE_MAX_LEN);
}
const LICENSE_PLATE_FORMAT_ERROR =
  "5–12 characters (A–Z, 0–9, space or hyphen), with at least 2 letters and 2 numbers.";

function getTodayDateString(): string {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function getTomorrowDateString(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** Min date for Permit End: must be after today and after Permit Start Date */
function getMinPermitEndDate(permitStartDate: string): string {
  const tomorrow = getTomorrowDateString();
  if (!permitStartDate) return tomorrow;
  const start = new Date(permitStartDate);
  start.setDate(start.getDate() + 1);
  const dayAfterStart = start.getFullYear() + '-' + String(start.getMonth() + 1).padStart(2, '0') + '-' + String(start.getDate()).padStart(2, '0');
  return dayAfterStart > tomorrow ? dayAfterStart : tomorrow;
}

function isAddCategoryOption(option: string | { addValue: string } | { isAddCategoryTrigger: true }): option is { addValue: string } {
  return typeof option === 'object' && option !== null && 'addValue' in option;
}

const ADD_CATEGORY_TRIGGER = { isAddCategoryTrigger: true as const };
function isAddCategoryTrigger(option: unknown): option is { isAddCategoryTrigger: true } {
  return typeof option === 'object' && option !== null && (option as { isAddCategoryTrigger?: boolean }).isAddCategoryTrigger === true;
}

const EMPTY_STATE_OPTION = { emptyState: true as const };
function isEmptyStateOption(option: unknown): option is { emptyState: true } {
  return typeof option === 'object' && option !== null && (option as { emptyState?: boolean }).emptyState === true;
}

type CategoryDisplayOption = string | { addValue: string } | { isAddCategoryTrigger: true } | { emptyState: true };

interface AddANPRDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (anprData?: any) => void;
  editData?: ANPRData | null;
  /** Existing license plates for uniqueness check (when editing, exclude current record's plate) */
  existingLicensePlates?: string[];
}

export function AddANPRDialog({ open, onClose, onSave, editData, existingLicensePlates = [] }: AddANPRDialogProps) {
  const [licensePlate, setLicensePlate] = useState('');
  const [owner, setOwner] = useState('');
  const [category, setCategory] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [categoryList, setCategoryList] = useState<VehicleCategoryApiItem[]>([]);
  const [categoryInputValue, setCategoryInputValue] = useState('');
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const categoryInputRef = React.useRef<HTMLInputElement>(null);
  const [licensePlateError, setLicensePlateError] = useState<string | null>(null);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [permitStartDate, setPermitStartDate] = useState('');
  const [permitEndDate, setPermitEndDate] = useState('');
  const [permitStartDateError, setPermitStartDateError] = useState<string | null>(null);
  const [permitEndDateError, setPermitEndDateError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Match Add Face: info icon only when row hovered or field focused */
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      setLicensePlate(editData.licensePlate || '');
      setOwner((editData.vehicleOwner || '').replace(/[^a-zA-Z\s]/g, ''));
      const cat = editData.category || 'Staff';
      setCategory(cat);
      setCategoryInputValue(cat);
      setPermitStartDate(editData.permitStartDate ?
        new Date(editData.permitStartDate).toISOString().split('T')[0] : '');
      setPermitEndDate(editData.permitEndDate ?
        new Date(editData.permitEndDate).toISOString().split('T')[0] : '');
      setAddress((editData as any).address || '');
      setDescription((editData as any).description || '');
    }
  }, [editData]);

  // When dialog opens for Add: default Permit Start to today; sync category input
  useEffect(() => {
    if (open && !editData) {
      setPermitStartDate(getTodayDateString());
      setCategoryInputValue(category);
    }
  }, [open]);

  // Fetch vehicle categories when Add Vehicle popup opens
  useEffect(() => {
    if (!open) return;
    const base = USE_CASE_CONFIG_API_BASE.trim();
    if (!base) {
      setCategoryOptions([]);
      setCategory('');
      setCategoryInputValue('');
      setCategoriesError('API base URL is not configured.');
      return;
    }
    setCategoriesError(null);
    setCategoriesLoading(true);
    const url = `${base.replace(/\/$/, '')}/use-case-configuration/vehicle-categories`;
    fetch(url, {
      headers: { Accept: 'application/json', ...userTimezoneHeaders() },
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || 'Failed to load vehicle categories');
        return res.json();
      })
      .then((response: { data?: VehicleCategoryApiItem[] }) => {
        const data = response?.data;
        const list = Array.isArray(data) ? data : [];
        const names = list.map((c) => (c.name ?? '').trim()).filter(Boolean);
        setCategoryOptions(names);
        setCategoryList(list);
        if (names.length === 0) {
          setCategory('');
          setCategoryInputValue('');
        }
        setCategoriesError(null);
      })
      .catch((err) => {
        setCategoryOptions([]);
        setCategoryList([]);
        setCategory('');
        setCategoryInputValue('');
        setCategoriesError(err?.message || 'Failed to load vehicle categories');
      })
      .finally(() => setCategoriesLoading(false));
  }, [open]);

  const refetchCategories = React.useCallback((): Promise<string[]> => {
    const base = USE_CASE_CONFIG_API_BASE.trim();
    const url = `${base.replace(/\/$/, '')}/use-case-configuration/vehicle-categories`;
    return fetch(url, {
      headers: { Accept: 'application/json', ...userTimezoneHeaders() },
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || 'Failed to load vehicle categories');
        return res.json();
      })
      .then((response: { data?: VehicleCategoryApiItem[] }) => {
        const data = response?.data;
        const list = Array.isArray(data) ? data : [];
        const names = list.map((c) => (c.name ?? '').trim()).filter(Boolean);
        setCategoryOptions(names);
        setCategoryList(list);
        return names;
      });
  }, []);

  const handleClose = () => {
    setLicensePlate('');
    setOwner('');
    setCategory('');
    setCategoryOptions([]);
    setCategoryList([]);
    setCategoryInputValue('');
    setCategoryError(null);
    setCategoriesLoading(false);
    setCategoriesError(null);
    setCreateCategoryLoading(false);
    setCategoryDropdownOpen(false);
    setSaveLoading(false);
    setSaveError(null);
    setLicensePlateError(null);
    setOwnerError(null);
    setPermitStartDate('');
    setPermitEndDate('');
    setPermitStartDateError(null);
    setPermitEndDateError(null);
    setShowRequiredErrors(false);
    setAddress('');
    setDescription('');
    setHoveredField(null);
    setActiveField(null);
    onClose();
  };

  const categoryDisplayOptions = React.useMemo((): CategoryDisplayOption[] => {
    if (!categoriesLoading && categoryOptions.length === 0) {
      return [EMPTY_STATE_OPTION];
    }
    const input = categoryInputValue.trim();
    const filtered = input
      ? categoryOptions.filter((o) => o.toLowerCase().includes(input.toLowerCase()))
      : categoryOptions;
    const exactMatch = input && categoryOptions.some((o) => o.toLowerCase() === input.toLowerCase());
    if (input && !exactMatch) {
      return [...filtered, { addValue: input }];
    }
    return filtered;
  }, [categoryOptions, categoryInputValue, categoriesLoading]);

  const createCategory = React.useCallback(
    (newCat: string) => {
      const name = newCat.trim();
      if (!name) {
        setCategoryError('Please enter a category name.');
        return;
      }
      const isDuplicate = categoryOptions.some((o) => o.toLowerCase() === name.toLowerCase());
      if (isDuplicate) {
        setCategoryError('This category name already exists.');
        return;
      }
      const base = USE_CASE_CONFIG_API_BASE.trim();
      if (!base) {
        setCategoryError('API base URL is not configured.');
        return;
      }
      setCategoryError(null);
      setCreateCategoryLoading(true);
      const url = `${base.replace(/\/$/, '')}/use-case-configuration/vehicle-categories`;
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify({ name }),
      })
        .then((res) => {
          if (res.status === 201) return res.json();
          if (res.status === 422) {
            return res.json().then((data: { detail?: Array<{ msg?: string }> }) => {
              const msg = data?.detail?.[0]?.msg ?? 'Validation failed';
              const e = new Error(msg) as Error & { is422?: boolean };
              e.is422 = true;
              throw e;
            });
          }
          throw new Error(res.statusText || 'Failed to create category');
        })
        .then((data: VehicleCategoryApiItem) => {
          const createdName = (data?.name ?? name).trim() || name;
          return refetchCategories().then(() => {
            setCategory(createdName);
            setCategoryInputValue(createdName);
            setCategoryError(null);
          });
        })
        .catch((err: Error & { is422?: boolean }) => {
          setCategoryError(err?.message ?? 'Failed to create category');
          if (err?.is422) {
            setTimeout(() => setCategoryDropdownOpen(true), 0);
          }
        })
        .finally(() => setCreateCategoryLoading(false));
    },
    [categoryOptions, refetchCategories]
  );

  const handleCategoryChange = (
    _: React.SyntheticEvent,
    value: string | { addValue: string } | { isAddCategoryTrigger: true } | { emptyState: true } | null
  ) => {
    setCategoryError(null);
    if (value === null) {
      setCategory('');
      return;
    }
    if (isAddCategoryTrigger(value) || isEmptyStateOption(value)) {
      setTimeout(() => categoryInputRef.current?.focus(), 0);
      return;
    }
    if (isAddCategoryOption(value)) {
      createCategory(value.addValue);
      return;
    }
    setCategory(value);
    setCategoryInputValue(value);
  };

  const handleSave = () => {
    setShowRequiredErrors(true);
    const plateTrimmed = licensePlate.trim();
    const ownerTrimmed = owner.trim();

    // Required fields must not be empty
    let hasError = false;
    if (!plateTrimmed) {
      setLicensePlateError('License plate is required and cannot be empty.');
      hasError = true;
    } else {
      if (!LICENSE_PLATE_REGEX.test(plateTrimmed)) {
        setLicensePlateError(LICENSE_PLATE_FORMAT_ERROR);
        hasError = true;
      } else {
        const others = existingLicensePlates.filter((p) => p.trim().toLowerCase() !== (editData?.licensePlate ?? '').trim().toLowerCase());
        const isDuplicate = others.some((p) => p.trim().toLowerCase() === plateTrimmed.toLowerCase());
        if (isDuplicate) {
          setLicensePlateError('This license plate is already registered.');
          hasError = true;
        } else {
          setLicensePlateError(null);
        }
      }
    }
    if (!ownerTrimmed) {
      setOwnerError('Vehicle owner is required.');
      hasError = true;
    } else if (!/^[a-zA-Z\s]+$/.test(ownerTrimmed)) {
      setOwnerError('Vehicle owner can only contain letters (A–Z, a–z) and spaces.');
      hasError = true;
    } else {
      setOwnerError(null);
    }
    if (!category.trim()) {
      setCategoryError('Category is required.');
      hasError = true;
    } else {
      setCategoryError(null);
    }
    if (!permitStartDate) {
      setPermitStartDateError('Permit start date is required.');
      hasError = true;
    } else {
      setPermitStartDateError(null);
    }
    if (!permitEndDate) {
      setPermitEndDateError('Permit end date is required.');
      hasError = true;
    } else if (permitStartDate && permitEndDate <= permitStartDate) {
      setPermitEndDateError('Permit end date must not be earlier than start date.');
      hasError = true;
    } else {
      const today = getTodayDateString();
      if (permitEndDate <= today) {
        setPermitEndDateError('Permit end date must be after today.');
        hasError = true;
      } else {
        setPermitEndDateError(null);
      }
    }

    if (hasError || !isValid) return;

    if (!editData) {
      const categoryName = (category || categoryInputValue || '').trim();
      const vehicleCategoryId = categoryList.find((c) => (c.name ?? '').trim() === categoryName)?.id;
      if (!vehicleCategoryId) {
        setCategoryError('Please select a category from the list.');
        return;
      }
      const base = USE_CASE_CONFIG_API_BASE.trim();
      if (!base) {
        setSaveError('API base URL is not configured.');
        return;
      }
      setSaveError(null);
      setSaveLoading(true);
      const url = `${base.replace(/\/$/, '')}/use-case-configuration/vehicles`;
      const body = {
        license_plate: plateTrimmed,
        vehicle_category_id: vehicleCategoryId,
        vehicle_owner: ownerTrimmed,
        permit_start_date: permitStartDate,
        permit_end_date: permitEndDate,
        address: address.trim() || undefined,
        description: description.trim() || undefined,
      };
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify(body),
      })
        .then((res) => {
          if (res.status === 201) return res.json();
          if (res.status === 422) {
            return res.json().then((data: { detail?: Array<{ msg?: string }> }) => {
              const msg = data?.detail?.[0]?.msg ?? 'Validation failed';
              const e = new Error(msg) as Error & { is422?: boolean };
              e.is422 = true;
              throw e;
            });
          }
          throw new Error(res.statusText || 'Failed to create vehicle');
        })
        .then(() => {
          toast.success('Vehicle added successfully', { position: 'bottom-center' });
          onSave();
          handleClose();
        })
        .catch((err: Error) => {
          setSaveError(err?.message ?? 'Failed to create vehicle');
        })
        .finally(() => setSaveLoading(false));
      return;
    }

    if (editData) {
      const categoryName = (category || categoryInputValue || '').trim();
      const vehicleCategoryId = categoryList.find((c) => (c.name ?? '').trim() === categoryName)?.id;
      if (!vehicleCategoryId) {
        setCategoryError('Please select a category from the list.');
        return;
      }
      const base = USE_CASE_CONFIG_API_BASE.trim();
      if (!base) {
        setSaveError('API base URL is not configured.');
        return;
      }
      setSaveError(null);
      setSaveLoading(true);
      const enrollmentId = encodeURIComponent(editData.id);
      const url = `${base.replace(/\/$/, '')}/use-case-configuration/vehicles/${enrollmentId}`;
      const body = {
        license_plate: plateTrimmed,
        vehicle_category_id: vehicleCategoryId,
        vehicle_owner: ownerTrimmed,
        permit_start_date: permitStartDate,
        permit_end_date: permitEndDate,
        address: address.trim() || undefined,
        description: description.trim() || undefined,
      };
      fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify(body),
      })
        .then((res) => {
          if (res.status === 200) return res.json();
          if (res.status === 422) {
            return res.json().then((data: { detail?: Array<{ msg?: string }> }) => {
              const msg = data?.detail?.[0]?.msg ?? 'Validation failed';
              const e = new Error(msg) as Error & { is422?: boolean };
              e.is422 = true;
              throw e;
            });
          }
          throw new Error(res.statusText || 'Failed to update vehicle');
        })
        .then(() => {
          toast.success('Vehicle updated successfully', { position: 'bottom-center' });
          onSave();
          handleClose();
        })
        .catch((err: Error) => {
          setSaveError(err?.message ?? 'Failed to update vehicle');
        })
        .finally(() => setSaveLoading(false));
      return;
    }
  };

  // License plate: format and uniqueness (required, format, unique)
  const licensePlateTrimmed = licensePlate.trim();
  const licensePlateValid = React.useMemo(() => {
    if (!licensePlateTrimmed) return false;
    if (!LICENSE_PLATE_REGEX.test(licensePlateTrimmed)) return false;
    const others = existingLicensePlates.filter(
      (p) => p.trim().toLowerCase() !== (editData?.licensePlate ?? '').trim().toLowerCase()
    );
    return !others.some((p) => p.trim().toLowerCase() === licensePlateTrimmed.toLowerCase());
  }, [licensePlateTrimmed, existingLicensePlates, editData?.licensePlate]);

  // Permit End: must be after today and must not be earlier than Permit Start Date
  const permitEndDateValid = React.useMemo(() => {
    if (!permitEndDate) return false;
    const today = getTodayDateString();
    if (permitEndDate <= today) return false; // cannot be today or past
    if (permitStartDate && permitEndDate <= permitStartDate) return false; // must be > start
    return true;
  }, [permitStartDate, permitEndDate]);

  React.useEffect(() => {
    if (!permitEndDate) {
      setPermitEndDateError(null);
      return;
    }
    const today = getTodayDateString();
    if (permitEndDate <= today) {
      setPermitEndDateError('Permit end date must be after today.');
      return;
    }
    if (permitStartDate && permitEndDate <= permitStartDate) {
      setPermitEndDateError('Permit end date must not be earlier than start date.');
      return;
    }
    setPermitEndDateError(null);
  }, [permitStartDate, permitEndDate]);

  const categoryFilled = Boolean((category || categoryInputValue || '').trim());
  const isValid = Boolean(
    licensePlateTrimmed &&
    licensePlateValid &&
    owner.trim() &&
    categoryFilled &&
    permitStartDate &&
    permitEndDate &&
    permitEndDateValid
  );

  const hasChanges = React.useMemo(() => {
    if (!editData) return true;
    const editStart = editData.permitStartDate
      ? new Date(editData.permitStartDate).toISOString().split('T')[0]
      : '';
    const editEnd = editData.permitEndDate
      ? new Date(editData.permitEndDate).toISOString().split('T')[0]
      : '';
    return (
      licensePlate.trim() !== (editData.licensePlate ?? '').trim() ||
      owner.trim() !== (editData.vehicleOwner ?? '').trim() ||
      category.trim() !== (editData.category ?? '').trim() ||
      permitStartDate !== editStart ||
      permitEndDate !== editEnd ||
      (address || '').trim() !== ((editData as any).address ?? '').trim() ||
      (description || '').trim() !== ((editData as any).description ?? '').trim()
    );
  }, [editData, licensePlate, owner, category, permitStartDate, permitEndDate, address, description]);

  const canSave = editData ? (isValid && hasChanges) : isValid;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          boxShadow: '0px 25px 50px 0px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 61,
          borderBottom: '0.8px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: 700,
            color: '#101828',
            lineHeight: '28px',
          }}
        >
          {editData ? 'Edit Vehicle' : 'Add Vehicle'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            width: 20,
            height: 20,
            p: 0,
            color: '#99A1AF',
            '&:hover': {
              color: '#4A5565',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* License Plate and Owner - one line (hover wrapper = Face Recognition pattern) */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{ flex: 1, position: 'relative' }}
            onMouseEnter={() => setHoveredField('licensePlate')}
            onMouseLeave={() => setHoveredField(null)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#364153',
                  lineHeight: '20px',
                }}
              >
                License Plate <Box component="span" sx={{ color: '#FB2C36' }}>*</Box>
              </Typography>
              {(hoveredField === 'licensePlate' || activeField === 'licensePlate') && (
                <Tooltip
                  title="Required. 5–12 chars, ≥2 letters & ≥2 digits; only A–Z, 0–9, space, hyphen. Uppercased."
                  {...addVehicleFieldTooltipProps}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                    <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                  </span>
                </Tooltip>
              )}
            </Box>
            <TextField
              fullWidth
              value={licensePlate}
              onChange={(e) => {
                setLicensePlate(normalizeLicensePlateInput(e.target.value));
                setLicensePlateError(null);
              }}
              onFocus={() => setActiveField('licensePlate')}
              onBlur={() => {
                setActiveField((prev) => (prev === 'licensePlate' ? null : prev));
                const plateTrimmed = licensePlate.trim();
                if (!plateTrimmed) {
                  setLicensePlateError('License plate is required and cannot be empty.');
                  return;
                }
                if (!LICENSE_PLATE_REGEX.test(plateTrimmed)) {
                  setLicensePlateError(LICENSE_PLATE_FORMAT_ERROR);
                  return;
                }
                const others = existingLicensePlates.filter(
                  (p) => p.trim().toLowerCase() !== (editData?.licensePlate ?? '').trim().toLowerCase()
                );
                if (others.some((p) => p.trim().toLowerCase() === plateTrimmed.toLowerCase())) {
                  setLicensePlateError('This license plate is already registered.');
                  return;
                }
                setLicensePlateError(null);
              }}
              placeholder="e.g., AB-12CD (2+ letters, 2+ digits)"
              inputProps={{ maxLength: LICENSE_PLATE_MAX_LEN }}
              error={!!licensePlateError}
              helperText={licensePlateError ?? undefined}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 41,
                  borderRadius: '10px',
                  bgcolor: 'white',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  fontWeight: 600,
                  '& input::placeholder': {
                    color: 'rgba(33, 33, 33, 0.5)',
                    opacity: 1,
                    fontFamily: 'Arial, sans-serif',
                    fontWeight: 400,
                  },
                },
              }}
            />
          </Box>
          <Box
            sx={{ flex: 1, position: 'relative' }}
            onMouseEnter={() => setHoveredField('owner')}
            onMouseLeave={() => setHoveredField(null)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#364153',
                  lineHeight: '20px',
                }}
              >
              Vehicle Owner <Box component="span" sx={{ color: '#FB2C36' }}>*</Box>
            </Typography>
              {(hoveredField === 'owner' || activeField === 'owner') && (
                <Tooltip title="Enter the name of the vehicle owner" {...addVehicleFieldTooltipProps}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                    <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                  </span>
                </Tooltip>
              )}
            </Box>
            <TextField
              fullWidth
              value={owner}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                setOwner(value);
                setOwnerError(null);
              }}
              onFocus={() => setActiveField('owner')}
              onBlur={() => setActiveField((prev) => (prev === 'owner' ? null : prev))}
              placeholder="e.g., John Doe"
              error={!!ownerError}
              helperText={ownerError ?? undefined}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 41,
                  borderRadius: '10px',
                  bgcolor: 'white',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  '& input::placeholder': {
                    color: 'rgba(33, 33, 33, 0.5)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Category - Search and add */}
        <Box
          sx={{ position: 'relative' }}
          onMouseEnter={() => setHoveredField('category')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                color: '#364153',
                lineHeight: '20px',
              }}
            >
              Category <Box component="span" sx={{ color: '#FB2C36' }}>*</Box>
            </Typography>
            {(hoveredField === 'category' || activeField === 'category') && (
              <Tooltip
                title="Search or click Add Category to create a new one"
                {...addVehicleFieldTooltipProps}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                  <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                </span>
              </Tooltip>
            )}
          </Box>
          <Autocomplete<CategoryDisplayOption, false, false, true>
            freeSolo
            options={categoryDisplayOptions}
            value={category}
            inputValue={categoryInputValue}
            onInputChange={(_, value) => {
              setCategoryInputValue(value);
              setCategory(value || '');
              setCategoryError(null);
            }}
            onChange={handleCategoryChange}
            open={categoryDropdownOpen}
            onOpen={() => setCategoryDropdownOpen(true)}
            onClose={() => setCategoryDropdownOpen(false)}
            getOptionLabel={(option) =>
              isEmptyStateOption(option) ? 'No categories available' : isAddCategoryTrigger(option) ? 'Add Category' : isAddCategoryOption(option) ? `Add "${option.addValue}"` : String(option)
            }
            filterOptions={(x) => x}
            isOptionEqualToValue={(option, value) => {
              if (isEmptyStateOption(option)) return false;
              if (isAddCategoryTrigger(option)) return false;
              if (isAddCategoryOption(option)) return false;
              return String(option) === String(value);
            }}
            loading={categoriesLoading || createCategoryLoading}
            disabled={categoriesLoading || createCategoryLoading}
            renderOption={(props, option) => {
              if (isEmptyStateOption(option)) {
                return (
                  <li
                    {...props}
                    key="empty-state"
                    style={{
                      ...(props as React.HTMLAttributes<HTMLLIElement>).style,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 12,
                      padding: '12px 16px',
                    }}
                  >
                    <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                      No categories available
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        createCategory(categoryInputValue);
                        if (!categoryInputValue.trim()) {
                          setTimeout(() => categoryInputRef.current?.focus(), 0);
                        }
                      }}
                      disabled={createCategoryLoading}
                      sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2932E5',
                        border: '1px solid #2932E5',
                        borderRadius: '8px',
                        px: 2,
                        py: 1,
                        '&:hover': { bgcolor: 'rgba(41, 50, 229, 0.08)' },
                      }}
                    >
                      Add Category
                    </Button>
                  </li>
                );
              }
              if (isAddCategoryTrigger(option)) {
                return (
                  <li
                    {...props}
                    key="add-category-trigger"
                    style={{
                      ...(props as React.HTMLAttributes<HTMLLIElement>).style,
                      fontWeight: 600,
                      color: '#2932E5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                    Add Category
                  </li>
                );
              }
              if (isAddCategoryOption(option)) {
                return (
                  <li
                    {...props}
                    key={`add-${option.addValue}`}
                    style={{
                      ...(props as React.HTMLAttributes<HTMLLIElement>).style,
                      fontWeight: 600,
                      color: '#2932E5',
                    }}
                  >
                    + Add Category &quot;{option.addValue}&quot;
                  </li>
                );
              }
              return <li {...props} key={String(option)}>{option}</li>;
            }}
            renderInput={(params) => {
              const inputParams = params as typeof params & { onFocus?: (e: React.FocusEvent) => void; onBlur?: (e: React.FocusEvent) => void };
              return (
                <TextField
                  {...params}
                  onFocus={(e) => {
                    setActiveField('category');
                    inputParams.onFocus?.(e);
                  }}
                  onBlur={(e) => {
                    setActiveField((prev) => (prev === 'category' ? null : prev));
                    inputParams.onBlur?.(e);
                  }}
                  inputRef={(el) => {
                    categoryInputRef.current = el;
                    const pr = params as typeof params & { inputRef?: React.Ref<HTMLInputElement> };
                    if (pr.inputRef) {
                      if (typeof pr.inputRef === 'function') pr.inputRef(el);
                      else (pr.inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    }
                  }}
                  placeholder={categoriesLoading ? 'Loading categories...' : createCategoryLoading ? 'Creating category...' : categoryOptions.length === 0 ? 'Type to add a category' : 'Search or add category...'}
                  error={!!categoryError || !!categoriesError}
                  helperText={categoryError ?? categoriesError ?? undefined}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 41,
                      borderRadius: '10px',
                      bgcolor: 'white',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                    },
                  }}
                />
              );
            }}
          />
        </Box>

        {/* Permit Start Date and Permit End Date - one line */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{ flex: 1, position: 'relative' }}
            onMouseEnter={() => setHoveredField('permitStartDate')}
            onMouseLeave={() => setHoveredField(null)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#364153',
                  lineHeight: '20px',
                }}
              >
                Permit Start Date <Box component="span" sx={{ color: '#FB2C36' }}>*</Box>
              </Typography>
              {(hoveredField === 'permitStartDate' || activeField === 'permitStartDate') && (
                <Tooltip title="Start date of vehicle permit" {...addVehicleFieldTooltipProps}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                    <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                  </span>
                </Tooltip>
              )}
            </Box>
            <TextField
              fullWidth
              type="date"
              value={permitStartDate}
              onChange={(e) => {
                setPermitStartDate(e.target.value);
                setPermitStartDateError(null);
              }}
              onFocus={() => setActiveField('permitStartDate')}
              onBlur={() => setActiveField((prev) => (prev === 'permitStartDate' ? null : prev))}
              placeholder="mm/dd/yyyy"
              error={!!permitStartDateError}
              helperText={permitStartDateError ?? undefined}
              inputProps={{ max: '9999-12-31' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 41,
                  borderRadius: '10px',
                  bgcolor: 'white',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
          <Box
            sx={{ flex: 1, position: 'relative' }}
            onMouseEnter={() => setHoveredField('permitEndDate')}
            onMouseLeave={() => setHoveredField(null)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#364153',
                  lineHeight: '20px',
                }}
              >
                Permit End Date <Box component="span" sx={{ color: '#FB2C36' }}>*</Box>
              </Typography>
              {(hoveredField === 'permitEndDate' || activeField === 'permitEndDate') && (
                <Tooltip title="End date of vehicle permit" {...addVehicleFieldTooltipProps}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                    <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                  </span>
                </Tooltip>
              )}
            </Box>
            <TextField
              fullWidth
              type="date"
              value={permitEndDate}
              onChange={(e) => {
                setPermitEndDate(e.target.value);
              }}
              onFocus={() => setActiveField('permitEndDate')}
              onBlur={() => setActiveField((prev) => (prev === 'permitEndDate' ? null : prev))}
              placeholder="mm/dd/yyyy"
              error={!!permitEndDateError}
              helperText={permitEndDateError ?? undefined}
              inputProps={{
                min: getMinPermitEndDate(permitStartDate),
                max: '9999-12-31',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 41,
                  borderRadius: '10px',
                  bgcolor: 'white',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        </Box>

        {/* Address (optional) */}
        <Box>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                color: '#364153',
              lineHeight: '20px',
              mb: 1,
              display: 'block',
            }}
          >
            Address <Box component="span" sx={{ color: '#6B7280', fontWeight: 400 }}>(Optional)</Box>
          </Typography>
          <TextField
            fullWidth
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g., 123 Main St, Anytown, USA"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 41,
                borderRadius: '10px',
                bgcolor: 'white',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                '& input::placeholder': {
                  color: 'rgba(33, 33, 33, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Description (optional) */}
        <Box>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                color: '#364153',
              lineHeight: '20px',
              mb: 1,
              display: 'block',
            }}
          >
            Description <Box component="span" sx={{ color: '#6B7280', fontWeight: 400 }}>(Optional)</Box>
          </Typography>
          <TextField
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Personal vehicle"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 41,
                borderRadius: '10px',
                bgcolor: 'white',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                '& input::placeholder': {
                  color: 'rgba(33, 33, 33, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          height: 69,
          bgcolor: '#F9FAFB',
          borderTop: '0.8px solid #E5E7EB',
          borderRadius: '0 0 10px 10px',
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {saveError && (
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#B71C1C' }}>
            {saveError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
        <Button
          onClick={handleClose}
          disabled={saveLoading}
          sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            color: '#0A0A0A',
            bgcolor: 'white',
            border: '0.8px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            px: 2,
            py: 1,
            '&:hover': {
              bgcolor: '#F5F5F5',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave || saveLoading}
          sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            color: 'white',
            bgcolor: '#2932E5',
            border: '0.8px solid #2932E5',
            borderRadius: '8px',
            px: 2,
            py: 1,
            '&:hover': {
              bgcolor: '#1e28b5',
            },
            '&:active': {
              bgcolor: '#fff',
              color: '#2932E5',
              borderColor: '#2932E5',
            },
            '&:disabled': {
              bgcolor: '#E5E7EB',
              color: '#9E9E9E',
              borderColor: '#E5E7EB',
            },
            "&.Mui-disabled": {
              bgcolor: "#2932E5",
              color: "white",
              borderColor: "#2932E5",
              opacity: 1,
            },
          }}
        >
          {saveLoading ? 'Saving...' : editData ? 'Update' : 'Save'}
        </Button>
        </Box>
      </Box>
    </Dialog>
  );
}